import { createContext, useContext, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { SharedAccount } from '../types/api';
import { useAuth } from './AuthContext';

interface AccountContextType {
  currentAccountId: string | undefined;
  currentAccountName: string | undefined;
  isViewingSharedAccount: boolean;
  sharedAccounts: SharedAccount[];
  isLoadingSharedAccounts: boolean;
  setCurrentAccount: (accountId: string | undefined) => void;
}

const AccountContext = createContext<AccountContextType | null>(null);

export function AccountProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [currentAccountId, setCurrentAccountId] = useState<string | undefined>(undefined);

  const {
    data: sharedAccountsData,
    isLoading: isLoadingSharedAccounts,
  } = useQuery({
    queryKey: ['shared-accounts'],
    queryFn: () => api.getSharedAccounts(),
    enabled: !!user,
  });

  const sharedAccounts = sharedAccountsData?.accounts ?? [];

  // Reset to own account when user changes
  useEffect(() => {
    setCurrentAccountId(undefined);
  }, [user?.id]);

  const setCurrentAccount = (accountId: string | undefined) => {
    setCurrentAccountId(accountId);
  };

  // Determine if viewing a shared account
  const isViewingSharedAccount = currentAccountId !== undefined && currentAccountId !== user?.id;

  // Get the display name for current account
  const currentAccountName = isViewingSharedAccount
    ? sharedAccounts.find(a => a.sharedUserId === currentAccountId)?.sharedUserName ||
      sharedAccounts.find(a => a.sharedUserId === currentAccountId)?.sharedUserEmail
    : undefined;

  return (
    <AccountContext.Provider
      value={{
        currentAccountId,
        currentAccountName,
        isViewingSharedAccount,
        sharedAccounts,
        isLoadingSharedAccounts,
        setCurrentAccount,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
}

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) throw new Error('useAccount must be used within AccountProvider');
  return context;
};
